import { Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service'; // <-- IMPORTADO AQUI

export interface Menu {
  headTitle?: string;
  headTitle2?: string;
  path?: string;
  titleKey?: string;
  title?: string;
  icon?: string;
  type?: string;
  badgeValue?: string;
  badgeClass?: string;
  badgeText?: string;
  active?: boolean;
  selected?: boolean;
  bookmark?: boolean;
  children?: Menu[];
  children2?: Menu[];
  Menusub?: boolean;
  target?: boolean;
  menutype?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavService implements OnDestroy {
  private unsubscriber: Subject<any> = new Subject();
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);

  public search = false;
  public language = false;
  public megaMenu = false;
  public levelMenu = false;
  public megaMenuColapse: boolean = window.innerWidth < 1199;
  public collapseSidebar: boolean = window.innerWidth < 991;
  public horizontal: boolean = window.innerWidth >= 991;
  public fullScreen = false;
  active: any;

  constructor(private router: Router, private translate: TranslateService, private authService: AuthService) {
    this.setScreenWidth(window.innerWidth);

    fromEvent(window, 'resize')
      .pipe(debounceTime(1000), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        this.setScreenWidth(evt.target.innerWidth);
        if (evt.target.innerWidth < 991) {
          this.collapseSidebar = true;
          this.megaMenu = false;
          this.levelMenu = false;
        }
        if (evt.target.innerWidth < 1199) {
          this.megaMenuColapse = true;
        }
      });

    if (window.innerWidth < 991) {
      this.router.events.subscribe(() => {
        this.collapseSidebar = true;
        this.megaMenu = false;
        this.levelMenu = false;
      });
    }

    this.translateMenuTitles();
    this.translate.onLangChange.subscribe(() => this.translateMenuTitles());
  }

  /**
   * Reconstroi o menu com a role atual do token.
   * Chamar ao carregar o layout da area logada para evitar menu vazio por ordem de carregamento.
   */
  refreshMenu(): void {
    this.translateMenuTitles();
  }

  ngOnDestroy() {
    this.unsubscriber.next(null);
    this.unsubscriber.complete();
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }

  private capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private translateMenuTitles(): void {
    const filtered = this.filteredMenu();
    const role = this.authService.getUserRole();
    if (filtered.length === 0) {
      console.warn('[NavService] Menu filtrado vazio. Role lida do token:', role);
    }

    // Emite o menu filtrado imediatamente com titulos de fallback, para o menu nunca ficar vazio
    // enquanto translate.get() nao completa (evita menu vazio por async/falha de traducao).
    filtered.forEach((item) => {
      item.title = item.title || item.titleKey || '';
      if (item.children?.length) {
        item.children.forEach((child) => {
          child.title = child.title || child.titleKey || '';
        });
      }
    });
    this.items.next(filtered);

    const keys: string[] = [];
    filtered.forEach((item) => {
      if (item.titleKey) keys.push(item.titleKey);
      if (item.children?.length) {
        item.children.forEach((child) => {
          if (child.titleKey) keys.push(child.titleKey);
        });
      }
    });

    this.translate.get(keys).subscribe({
      next: (translations) => {
        const translated = this.filteredMenu().map((item) => {
          const key = item.titleKey;
          const text = key != null ? (translations[key] ?? key ?? '') : '';
          if (key) item.title = this.capitalize(String(text));
          if (item.children?.length) {
            item.children.forEach((child) => {
              const childKey = child.titleKey;
              const childText = childKey != null ? (translations[childKey] ?? childKey ?? '') : '';
              if (childKey) child.title = this.capitalize(String(childText));
            });
          }
          return item;
        });
        this.items.next(translated);
      },
      error: () => {
        // Se traducao falhar, o menu ja foi emitido acima com fallback; nao emite [].
      },
    });
  }

  MENUITEMS: Menu[] = [
    { icon: 'home-alt-2', path: 'dash', titleKey: 'home', type: 'link' },
    { icon: 'group', path: 'users', titleKey: 'users', type: 'link' },
    { icon: 'edit', path: 'assessment', titleKey: 'assessments', type: 'link' },
    { icon: 'id-card', path: 'purchase', titleKey: 'purchaseHistory', type: 'link' },
    { icon: 'buildings', path: 'organizations', titleKey: 'organizations', type: 'link' },
    { icon: 'shape-circle', path: 'groups', titleKey: 'groups', type: 'link' },
    { icon: 'atom', path: 'events', titleKey: 'events', type: 'link' },
    { icon: 'message-dots', path: '/paragraph/list', titleKey: 'paragraphs', type: 'link' },
    { icon: 'dollar', path: '/financial/coupon', titleKey: 'coupons', type: 'link' },
    { icon: 'atom', path: 'soar-ia', titleKey: 'Sofia', type: 'link' },
    { icon: 'edit', path: 'support', titleKey: 'support', type: 'link' },
    { icon: 'history', path: 'changelog', titleKey: 'changelog', type: 'link' },
    { icon: 'log-out', path: 'auth/login', titleKey: 'logout', type: 'link' },
  ];

  items = new BehaviorSubject<Menu[]>(this.MENUITEMS);

  /** Roles permitidas para cada recurso; comparacao case-insensitive. */
  private roleMatches(role: string | null, allowed: string[]): boolean {
    if (!role) return false;
    const r = role.toLowerCase();
    return allowed.some((a) => a.toLowerCase() === r);
  }

  /**
   * Filtra o menu conforme a role do usuario:
   * - Super Admin / Admin: todos os itens
   * - Master Practitioner / Practitioner / Partner / Institutional: tudo exceto eventos, paragrafos, cupons, historico de alteracoes
   * - Individual: apenas home, avaliacoes, historico de compras, suporte, sair
   */
  private filteredMenu(): Menu[] {
    const role = this.authService.getUserRole();
    const isAdmin = this.roleMatches(role, ['Super Admin', 'Admin']);
    const isPractitionerRole = this.roleMatches(role, ['Master Practitioner', 'Practitioner', 'Partner', 'Institutional', 'Institucional']);
    const isIndividual = this.roleMatches(role, ['Individual']);

    // Itens ocultos globalmente (para todas as roles)
    const globallyHiddenPaths = ['groups', 'changelog'];

    return this.MENUITEMS.filter((item) => {
      if (item.path != null && globallyHiddenPaths.includes(item.path)) return false;

      if (isIndividual) {
        const allowedPaths = ['dash', 'assessment', 'purchase', 'support', 'auth/login'];
        return item.path != null && allowedPaths.includes(item.path);
      }

      if (isPractitionerRole) {
        const hiddenPaths = ['events', '/paragraph/list', '/financial/coupon', 'changelog'];
        if (item.path != null && hiddenPaths.includes(item.path)) return false;
        return true;
      }

      if (isAdmin) return true;

      return true;
    });
  }
}
